import { useCallback, useContext } from 'react';
import { AxiosResponse } from 'axios';
import parseDataToBookingManagement from '../../components/BookingWidget/BookingManagment/bookingManagment.mapper';
import { BookingStepsContextState } from '../../context/bookingContext/bookingContext.types';
import {
  BookingConfig,
  BookingConfigEnum,
  BookingStepValueEnum,
  BookingSubStepValueEnum,
} from '../../components/BookingWidget/bookingSteps.interface';
import { BookingQuoteResponse } from '../../components/BookingWidget/BookingQuoteManagment/bookingQuoteResponse.interface';
import {
  BookingDataEnum,
  BookingDataResponse,
  BookingManagement,
  BookingManagementEnum,
} from '../../components/BookingWidget/BookingManagment/bookingManagment.interface';
import deepMerge from '../helpers/deepMergeObjects';
import useApi from './api.service';
import {
  DecodedSessionToken,
  SessionParams,
} from '../../context/sessionContext/sessionContext.interface';
import parseDataToQuote from '../../components/BookingWidget/BookingQuoteManagment/bookingQuoteManagment.mapper';
import authHelper from '../helpers/auth.helper';
import { CacheDataContext } from '../../context/cacheContext/cacheContext';

export interface BookingPaymentStatusResponse {
  status: 'pending' | 'done' | 'failed' | 'expired';
  policyId: string | null;
}

export interface SaveBookingAndQuoteParams {
  bookingSteps: BookingStepsContextState;
  quoteResponse: BookingQuoteResponse;
  bookingDataResponse: BookingDataResponse | null;
  bookingReferenceId: string | null;
  decodedSessionToken: DecodedSessionToken | null;
  agentEmail?: string;
}

interface UseBookingDataService {
  saveBooking: (
    bookingSteps: BookingStepsContextState,
    quoteResponse: BookingQuoteResponse,
    bookingDataResponse: BookingDataResponse | null,
    bookingReferenceId: string | null,
    decodedSessionToken: DecodedSessionToken | null,
  ) => Promise<any>;
  saveBookingAndQuote: (params: SaveBookingAndQuoteParams) => Promise<{
    quoteResponse: BookingQuoteResponse;
    bookingResponse: BookingDataResponse;
  }>;
  getBookingPaymentStatus: (
    bookingId: string,
  ) => Promise<BookingPaymentStatusResponse>;
}

const useBookingDataService = (
  bookingConfigData: BookingConfig,
  sessionParams: SessionParams | null,
): UseBookingDataService => {
  const { isAgent, setToken } = authHelper();
  const { getUrlParam } = useContext(CacheDataContext);
  const API = useApi(bookingConfigData, sessionParams!);
  const apiProxyUrl: string =
    bookingConfigData?.[BookingConfigEnum.DataCenter]?.cm360Endpoint;

  const saveBooking = useCallback(
    async (
      bookingSteps: BookingStepsContextState,
      quoteResponse: BookingQuoteResponse,
      bookingDataResponse: BookingDataResponse | null,
      bookingReferenceId: string | null,
      decodedSessionToken: DecodedSessionToken | null,
    ): Promise<any> => {
      const bookingData: BookingManagement = parseDataToBookingManagement(
        bookingSteps,
        bookingConfigData,
        quoteResponse,
        bookingDataResponse,
        bookingReferenceId,
        decodedSessionToken,
      );

      if (bookingDataResponse) {
        // eslint-disable-next-line no-param-reassign
        delete bookingDataResponse[BookingManagementEnum.BookingData][
          BookingDataEnum.ExpireDate
        ];
        const mergedData = deepMerge({}, bookingDataResponse, bookingData);
        const bookingId =
          mergedData[BookingManagementEnum.BookingData][BookingDataEnum.Id];
        const url = `${apiProxyUrl}/booking/${bookingId}`;

        const medicalDisclaimerConsent =
          bookingSteps[BookingStepValueEnum.QuotationInformation]?.[
            BookingSubStepValueEnum.MedicalDisclaimerConsent
          ];

        if (medicalDisclaimerConsent) {
          mergedData.consents = [
            {
              usages: [
                {
                  type: 'MEDICAL_DISCLAIMER_CONSENT',
                  version: 1,
                  isAgentExclusive: false,
                },
              ],
            },
            ...(bookingDataResponse[BookingManagementEnum.Consents] || []),
          ];
        }

        if (bookingSteps.quotationForm.tripCost) {
          mergedData.bookingData.tripCost = bookingSteps.quotationForm.tripCost;
        }

        if (bookingReferenceId) {
          mergedData.bookingData.externalId = bookingReferenceId;
        }

        const payload = mergedData;
        const response: AxiosResponse = await API.put(url, payload);
        if (response.headers['x-amzn-remapped-authorization'] && !isAgent()) {
          setToken(
            response.headers['x-amzn-remapped-authorization'].replace(
              'Bearer ',
              '',
            ),
          );
        }
        return response.data;
      }

      const url = `${apiProxyUrl}/booking`;
      const payload = parseDataToBookingManagement(
        bookingSteps,
        bookingConfigData,
        quoteResponse,
        bookingDataResponse,
        bookingReferenceId,
        decodedSessionToken,
      );

      const response: AxiosResponse = await API.post(url, payload);
      const anonymousToken = response.headers['x-amzn-remapped-authorization'];
      if (anonymousToken && !isAgent()) {
        setToken(anonymousToken);
      }
      return response.data;
    },
    [isAgent, setToken],
  );

  const saveBookingAndQuote = useCallback(
    async ({
      bookingSteps,
      quoteResponse,
      bookingDataResponse,
      bookingReferenceId,
      decodedSessionToken,
      agentEmail,
    }: SaveBookingAndQuoteParams): Promise<{
      quoteResponse: BookingQuoteResponse;
      bookingResponse: BookingDataResponse;
    }> => {
      const quotePayload = parseDataToQuote(
        bookingSteps,
        bookingConfigData,
        {
          agentEmail,
        },
        decodedSessionToken,
        bookingDataResponse,
        quoteResponse,
        bookingReferenceId,
      );

      const bookingPayload = parseDataToBookingManagement(
        bookingSteps,
        bookingConfigData,
        quoteResponse,
        bookingDataResponse,
        bookingReferenceId,
        decodedSessionToken,
      );
      const { psClient } = bookingConfigData[BookingConfigEnum.Channel];

      const bookingId =
        bookingDataResponse?.[BookingManagementEnum.BookingData]?.[
          BookingDataEnum.Id
        ];
      const url = `${apiProxyUrl}/booking/${bookingId}/quote`;

      const response = await API.post(
        url,
        {
          pnsRequest: {
            ...quotePayload,
            catalogVersion: bookingDataResponse?.bookingData.catalogVersion,
          },
          bookingRequest: bookingPayload,
        },
        {
          headers: {
            'Client-Id': psClient,
          },
        },
      );

      if (response.headers['x-amzn-remapped-authorization'] && !isAgent()) {
        setToken(
          response.headers['x-amzn-remapped-authorization'].replace(
            'Bearer ',
            '',
          ),
        );
      }
      return response.data;
    },
    [isAgent, setToken],
  );

  const getBookingPaymentStatus = useCallback(
    async (bookingId: string): Promise<BookingPaymentStatusResponse> => {
      const secret: string | null = getUrlParam('secret');

      const response = await API.get(
        `${apiProxyUrl}/booking/${bookingId}/payment-status${
          secret ? `?secret=${secret}` : ''
        }`,
      );

      return response.data;
    },
    [API, apiProxyUrl],
  );

  return {
    saveBooking,
    saveBookingAndQuote,
    getBookingPaymentStatus,
  };
};

export default useBookingDataService;
